import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { YearProvider } from './context/YearContext';
import PrivateRoute from './components/PrivateRoute';
import { initGA, trackPageView } from './utils/analytics';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdministratorDashboard = lazy(() => import('./pages/administrator/AdministratorDashboard'));
const InstitutionDashboard = lazy(() => import('./pages/institution/InstitutionDashboard'));

// Loading component
const LoadingSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <CircularProgress sx={{ color: '#8B1538' }} />
  </Box>
);

const theme = createTheme({
  palette: {
    primary: {
      main: '#8B1538',
      dark: '#6B0F2A',
      light: '#A83A5A',
    },
    secondary: {
      main: '#FFFFFF',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on 403 (permission denied) or 401 (unauthorized) errors
        if (error?.response?.status === 403 || error?.response?.status === 401) {
          return false;
        }
        return failureCount < 1;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes - cache data for 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
      onError: (error) => {
        // Suppress 404 errors for residents endpoints and permission errors
        if (error?.response?.status === 404 && error?.config?.url?.includes('/residents')) {
          return; // Don't log 404 errors for residents
        }
        // Only log non-permission errors to console
        if (error?.response?.status !== 403 && error?.response?.status !== 401) {
          console.error('Query error:', error);
        }
      },
    },
  },
});

// Component to track page views
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function AppRoutes() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Refetch all queries when user changes (after login/logout)
  useEffect(() => {
    if (user) {
      // User logged in - invalidate all queries to force refresh
      queryClient.invalidateQueries();
    } else {
      // User logged out - clear all queries
      queryClient.clear();
    }
  }, [user, queryClient]);

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <PageViewTracker />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/admin/*"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/administrator/*"
          element={
            <PrivateRoute allowedRoles={['administrator']}>
              <AdministratorDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/institution/*"
          element={
            <PrivateRoute allowedRoles={['institution']}>
              <InstitutionDashboard />
            </PrivateRoute>
          }
        />
      <Route
        path="/"
        element={
          user ? (
            <Navigate
              to={
                user.role === 'admin'
                  ? '/admin'
                  : user.role === 'administrator'
                  ? '/administrator'
                  : '/institution'
              }
              replace
            />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    // Initialize Google Analytics on app mount
    initGA();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <YearProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <AppRoutes />
            </Router>
          </YearProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;

